import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { PlatterDetails } from "@/components/PlatterDetails";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Gift } from "lucide-react";
import { LOYALTY_ENABLED } from "@/lib/features";
import { ProfileEditor } from "@/components/ProfileEditor";
import { MarketingPreferences } from "@/components/MarketingPreferences";
import { statusLabel } from "@/lib/ordering";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  preparing: "bg-blue-100 text-blue-800",
  ready: "bg-emerald-100 text-emerald-800",
  completed: "bg-neutral-200 text-neutral-700",
  cancelled: "bg-red-100 text-red-700",
};

export default function Account() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loyalty, setLoyalty] = useState(null);

  useEffect(() => {
    api.get("/orders/mine").then((r) => setOrders(r.data)).finally(() => setLoading(false));
    if (LOYALTY_ENABLED) {
      api.get("/loyalty/me").then((r) => setLoyalty(r.data));
    }
  }, []);

  const points = loyalty?.points ?? 0;
  const nextReward = Math.max(0, 500 - (points % 500));
  const availableRewards = Math.floor(points / 500);

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
        <div>
          <p className="eyebrow mb-2">Account</p>
          <h1 className="font-display font-bold text-4xl tracking-tight" data-testid="account-name">Hi, {user?.name}</h1>
          <p className="text-neutral-500 mt-1">{user?.email}{user?.phone ? ` · ${user.phone}` : ""}</p>
        </div>
        <ProfileEditor />
      </div>

      {/* Loyalty card */}
      {LOYALTY_ENABLED && (
      <div className="relative overflow-hidden rounded-3xl p-8 mb-10 bg-gradient-to-br from-[hsl(var(--primary))] to-[#c8351f] text-white" data-testid="loyalty-card">
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10"/>
        <div className="absolute right-16 bottom-4 w-24 h-24 rounded-full bg-white/10"/>
        <div className="relative flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur rounded-full px-3 py-1 text-xs uppercase tracking-widest font-semibold">
              <Sparkles className="w-3.5 h-3.5"/> Sandar Rewards
            </div>
            <div className="mt-4 font-display font-bold text-5xl leading-none" data-testid="loyalty-points">{points.toLocaleString()}<span className="text-xl ml-2 font-normal opacity-80">pts</span></div>
            <p className="mt-3 text-white/85 text-sm max-w-md leading-relaxed">
              {availableRewards > 0
                ? <>You have <span className="font-bold">{availableRewards} reward{availableRewards > 1 ? "s" : ""}</span> ready — redeem at checkout for <span className="font-bold">${availableRewards * 5}</span> off.</>
                : <>Earn <span className="font-bold">10 pts</span> for every $1 spent. <span className="font-bold">{nextReward}</span> more pts unlocks $5 off.</>}
            </p>
          </div>
          <div className="text-right">
            <div className="inline-flex items-center gap-2 bg-white/95 text-[hsl(var(--primary))] rounded-2xl px-5 py-4">
              <Gift className="w-5 h-5"/>
              <div className="text-left">
                <div className="text-xs uppercase tracking-wider font-semibold">Available</div>
                <div className="font-display font-bold text-2xl" data-testid="loyalty-rewards-count">{availableRewards} × $5</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      <h2 className="font-display font-semibold text-2xl mb-6">Your Orders</h2>
      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_,i) => <div key={`orders-skeleton-${i}`} className="h-24 bg-neutral-100 rounded-2xl animate-pulse"/>)}</div>
      ) : orders.length === 0 ? (
        <div className="text-neutral-500 text-center py-16 border-2 border-dashed border-neutral-200 rounded-3xl" data-testid="account-no-orders">No orders yet.</div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} className="bg-white p-5 rounded-2xl border border-neutral-200/70" data-testid={`order-${o.id}`}>
              <div className="flex justify-between items-center flex-wrap gap-3">
                <div>
                  <div className="font-mono text-sm text-neutral-500">{o.order_number}</div>
                  <div className="text-sm text-neutral-500 mt-1">{new Date(o.created_at).toLocaleString()}</div>
                </div>
                <Badge className={STATUS_STYLES[o.status] || ""}>{statusLabel(o.status)}</Badge>
                <div className="text-right">
                  <div className="font-display font-bold text-xl">${o.total.toFixed(2)}</div>
                  {LOYALTY_ENABLED && o.points_earned ? <div className="text-xs text-emerald-600">+{o.points_earned} pts</div> : null}
                  {LOYALTY_ENABLED && o.points_redeemed ? <div className="text-xs text-[hsl(var(--primary))]">−{o.points_redeemed} pts redeemed</div> : null}
                </div>
              </div>
              <div className="mt-3 text-sm text-neutral-600">
                {o.items.map((item) => (
                  <div key={item.cart_line_id || item.item_id} className="mb-2 last:mb-0">
                    <p>{item.quantity}× {item.name}</p>
                    <PlatterDetails platter={item.platter} compact />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <MarketingPreferences />
    </div>
  );
}
